import type { SVGProps } from 'react';

/**
 * Íconos de trazo, `stroke-width: 1.8`, `currentColor`.
 *
 * Son SVG inline y no una librería: el rediseño usa nueve íconos y traerse un paquete entero para
 * eso sería pagar un bundle por comodidad. Todos heredan el color del contexto, así que el estado
 * activo de la navegación no necesita variantes.
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 21, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function TodayIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="5" width="18" height="16" rx="4" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <circle cx="12" cy="15.5" r="2" />
    </Icon>
  );
}

export function HistoryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
      <path d="M3 4v4h4" />
      <path d="M12 7.5V12l3 2" />
    </Icon>
  );
}

export function CameraIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 8.5h2.6l1.4-2.2h8l1.4 2.2H20a1.5 1.5 0 0 1 1.5 1.5v7.5A1.5 1.5 0 0 1 20 19H4a1.5 1.5 0 0 1-1.5-1.5V10A1.5 1.5 0 0 1 4 8.5Z" />
      <circle cx="12" cy="13.5" r="3.4" />
    </Icon>
  );
}

export function BodyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="7" width="18" height="10" rx="3" />
      <path d="M7.5 7v3M12 7v4.5M16.5 7v3" />
    </Icon>
  );
}

export function AnalysisIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="M8.5 19v-5M13 19V8.5M17.5 19v-7.5" />
    </Icon>
  );
}

export function BackIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M15 5l-7 7 7 7" />
    </Icon>
  );
}

export function ForwardIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 5l7 7-7 7" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </Icon>
  );
}

export function EditIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20h4l10-10a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5 4 20Z" />
    </Icon>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </Icon>
  );
}

export function BarcodeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 6v12M7.5 6v12M11 6v8M14.5 6v12M18 6v8M20.5 6v12" />
    </Icon>
  );
}

/** La galería del teléfono: un marco con un sol y un horizonte. */
export function GalleryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M3 16.5l4.5-4 4 3.5 3.5-3L21 17" />
    </Icon>
  );
}

export function FlashIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13 3l-7 10h5l-1 8 7-10h-5l1-8Z" />
    </Icon>
  );
}

/**
 * La cuenta: una persona.
 *
 * Persona y no un engranaje. Detrás de esta puerta están el perfil, los objetivos, la exportación
 * de los datos y la baja — cosas que son de la persona, no ajustes de la aplicación. Un engranaje
 * diría «configuración» y haría dudar a quien busca sus propios datos.
 */
export function AccountIcon({ size, ...rest }: IconProps) {
  return (
    <Icon size={size} {...rest}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" />
    </Icon>
  );
}
